// deps
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";

// internal deps
import { Image } from "@swiggy-private/dls-web-core/dist/components/Image";
import { HalfCard } from "@swiggy-private/dls-web-core/dist/components/HalfCard";
import { CROSS_SELL_TEST_IDS } from "@swiggy-private/cart-test-ids";
import { VEG_TYPE, ItemPresentationAttributes } from "@swiggy-private/cart-types";
import { ElementVisibilitySensor } from "@swiggy-private/dls-web-core/dist/components/ElementVisibilitySensor";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";

// widgets
import { FoodSymbolIconSvg } from "@cart-ui/widgets/food-symbol-icon";
import { CrossSellM2AddItem } from "@cart-ui/widgets/cross-sell-m2-add-item";

// constants
import {
    CROSS_SELL_M2_ITEM_PLACEHOLDER,
    containerIdForCrossSellM2ItemDetailsBS,
    TEXTS,
    TICKER_REPEAT_COUNT,
    ACCESSIBILITY_TEXTS,
} from "@cart-ui/constants";

// components
import { CrossSellM2ItemDetails } from "@cart-ui/components/cross-sell-m2-item-details";
import { CrossSellM2ItemDetailsBSContainer } from "@cart-ui/components/cross-sell-m2-item-details-bs-container";

// styles
import {
    Container,
    AddButtonContainer,
    TextContainer,
    Details,
    ImageContainer,
    Name,
    FoodIconContainer,
    BasePriceContainer,
    FlexContainer,
    SurpriseContainer,
    TickerWrapper,
    TickerContent,
    TickerItem,
    LockIconContainer,
} from "./CrossSellM2Item.style";

import { default as LockFilledSmoothIcon } from "@swiggy-private/dls-web-icons/dist/web/generic/lockFilledSmooth/12";
import { useTheme } from "@swiggy-private/dls-web-theme/dist/components/ThemeProvider/use-theme";

// helpers
import { isEmpty, format } from "@swiggy-private/js-utils";
import { addWordBreaks, resolveFlipTextWithItemValues } from "./utils";
import { priceInRupee } from "@cart-ui/helpers/Price";
import { isPopFlow } from "@swiggy-private/checkout-core";

import UnlockPopup from "./UnlockPopup";

export interface ICrossSellM2ItemProps {
    itemId: number;
    name: string;
    position: number;
    basePrice: number;
    finalPrice: number;
    imageId?: string;
    vegType: VEG_TYPE;
    onAddButtonClick: () => void;
    description: string | undefined;
    onClickItemDetailsCarousel: () => void;
    index: number;
    isFreebieOptInItem?: boolean;
    onImpressionCrossSellM2Item: (item: string, basePrice?: number, slashedPrice?: number, position?: string) => void;
    onImpressionCarouselItemDetailsPopup?: (itemId: string, index: string) => void;
    isLocked?: boolean;
    unlockAmount?: number;
    onLockIconClick?: VoidFunction; // Analytics callback
    onExploreMenuClick?: VoidFunction; // Navigate to menu callback
    itemPresentationAttributes?: ItemPresentationAttributes;
    itemDiscountPercentage?: number;
    onImpressionMagicItemsUnlockPopup?: VoidFunction;
    onClickDismissMagicItemsUnlockPopup?: VoidFunction;
    onClickExploreMenuMagicItems?: VoidFunction;
    isSnaccStorefrontV2?: boolean;
    tickerResetKey?: number; // Key to reset ticker animation when cart updates
}

const CrossSellM2Item = ({
    itemId,
    name,
    basePrice,
    position,
    finalPrice,
    imageId,
    vegType,
    onAddButtonClick,
    description,
    onClickItemDetailsCarousel,
    index,
    isFreebieOptInItem,
    onImpressionCrossSellM2Item,
    onImpressionCarouselItemDetailsPopup,
    isLocked = false, // Default false means unlocked
    unlockAmount = 40,
    onLockIconClick,
    onExploreMenuClick,
    itemPresentationAttributes,
    itemDiscountPercentage,
    onImpressionMagicItemsUnlockPopup,
    onClickDismissMagicItemsUnlockPopup,
    onClickExploreMenuMagicItems,
    isSnaccStorefrontV2,
    tickerResetKey,
}: ICrossSellM2ItemProps) => {
    const [showBottomSheet, setShowBottomSheet] = useState(false);
    const [showUnlockPopup, setShowUnlockPopup] = useState(false);
    const itemNameRef = useRef<HTMLSpanElement>(null);
    const isPopCart = isPopFlow();
    const theme = useTheme();

    // Resolve flip text per-item
    const flipTextItems = useMemo(
        () => resolveFlipTextWithItemValues(itemPresentationAttributes?.flip_text, itemDiscountPercentage),
        [itemPresentationAttributes?.flip_text, itemDiscountPercentage],
    );

    // Handler for lock icon click
    const handleLockIconClick = useCallback(() => {
        onLockIconClick?.(); // Fire analytics
        setShowUnlockPopup(true);
    }, [onLockIconClick]);

    // Handler for closing unlock popup
    const handleCloseUnlockPopup = useCallback(() => {
        setShowUnlockPopup(false);
    }, []);

    // Handler for explore menu button
    const handleExploreMenu = useCallback(() => {
        onExploreMenuClick?.();
    }, [onExploreMenuClick]);

    const openBottomSheet = useCallback(() => {
        onClickItemDetailsCarousel();
        setShowBottomSheet(true);
    }, [onClickItemDetailsCarousel, isPopCart, isSnaccStorefrontV2]);

    const handleClose = useCallback(() => {
        setShowBottomSheet(false);
    }, []);

    const onImageClick = useCallback(() => {
        openBottomSheet();
    }, [openBottomSheet]);

    const accessibilityTextDetails = `${name} and price is Rs.${priceInRupee(finalPrice)}, tap for more details`;

    // Create unique and descriptive aria-label for add button
    const accessibilityTextAddButton = useMemo(() => {
        if (isFreebieOptInItem && finalPrice === 0) {
            return format(ACCESSIBILITY_TEXTS.ADD_ITEM_FOR_FREE, name);
        }
        return format(ACCESSIBILITY_TEXTS.ADD_ITEM_FOR_PRICE, name, priceInRupee(finalPrice));
    }, [name, finalPrice, isFreebieOptInItem]);

    const onCardVisibilityChange = useCallback(
        (isVisible: boolean) => {
            if (isVisible) {
                onImpressionCrossSellM2Item(itemId?.toString(), basePrice, finalPrice, String(position));
            }
        },
        [itemId, basePrice, finalPrice, onImpressionCrossSellM2Item, position],
    );

    useEffect(() => {
        const formattedName = addWordBreaks(name, isPopCart);
        if (!isEmpty(itemNameRef.current)) {
            itemNameRef.current.innerHTML = formattedName;
        }
    }, [name, itemNameRef, isPopCart]);

    // Fire impression event when bottom sheet opens
    useEffect(() => {
        if (showBottomSheet && onImpressionCarouselItemDetailsPopup) {
            onImpressionCarouselItemDetailsPopup(String(itemId), String(position));
        }
    }, [showBottomSheet, onImpressionCarouselItemDetailsPopup, itemId, position]);

    return (
        <ElementVisibilitySensor senseOnce onVisibilityChange={onCardVisibilityChange}>
            <Container
                data-testid={CROSS_SELL_TEST_IDS.PILL_CONTAINER}
                isPopCart={isPopCart}
                hasFlipText={!isEmpty(flipTextItems)}
                isSnaccStorefrontV2={isSnaccStorefrontV2}
            >
                <TextContainer
                    onClick={openBottomSheet}
                    aria-label={accessibilityTextDetails}
                    data-testid={CROSS_SELL_TEST_IDS.ITEM_DETAILS}
                    isSnaccStorefrontV2={isSnaccStorefrontV2}
                >
                    <Details>
                        <Name
                            variant="V2.body3.neutral.regular"
                            color="text_High_Emphasis"
                            maxLines={2}
                            data-testid={CROSS_SELL_TEST_IDS.ITEM_NAME}
                            isPopCart={isPopCart}
                        >
                            <FoodIconContainer>
                                <FoodSymbolIconSvg vegType={vegType} />
                            </FoodIconContainer>

                            <span ref={itemNameRef}></span>
                        </Name>
                    </Details>

                    {!isEmpty(flipTextItems) && (
                        <SurpriseContainer>
                            <TickerWrapper>
                                <TickerContent key={tickerResetKey} shouldAnimate={flipTextItems.length > 1}>
                                    {flipTextItems.length > 1 ? (
                                        // Repeat items for continuous scroll effect when multiple items
                                        [...Array(TICKER_REPEAT_COUNT)].map((_, repeatIndex) => (
                                            <React.Fragment key={repeatIndex}>
                                                {flipTextItems.map((item, index) => (
                                                    <TickerItem
                                                        key={`${repeatIndex}-${index}`}
                                                        variant="V2.caption.neutral.bold"
                                                        color={item.color}
                                                    >
                                                        {item.text}
                                                    </TickerItem>
                                                ))}
                                            </React.Fragment>
                                        ))
                                    ) : (
                                        // Single item - display statically
                                        <TickerItem variant="V2.caption.neutral.bold" color={flipTextItems[0].color}>
                                            {flipTextItems[0].text}
                                        </TickerItem>
                                    )}
                                </TickerContent>
                            </TickerWrapper>
                        </SurpriseContainer>
                    )}
                    <FlexContainer>
                        {basePrice !== finalPrice && (
                            <BasePriceContainer
                                variant="V2.overline.neutral.medium"
                                color="text_Low_Emphasis"
                                data-testid={CROSS_SELL_TEST_IDS.ITEM_BASE_PRICE}
                            >
                                {`₹${priceInRupee(basePrice)}`}
                            </BasePriceContainer>
                        )}

                        <Text
                            variant="V2.body3.neutral.medium"
                            color="text_Med_Emphasis"
                            data-testid={CROSS_SELL_TEST_IDS.ITEM_PRICE}
                        >
                            {/* TODO: isFreebieOptInItem check can be removed later if same requirement comes for super app */}
                            {isFreebieOptInItem && finalPrice === 0 ? TEXTS.FREE : `₹${priceInRupee(finalPrice)}`}
                        </Text>
                    </FlexContainer>
                </TextContainer>

                <ImageContainer
                    isPopCart={isPopCart}
                    data-testid={CROSS_SELL_TEST_IDS.ITEM_IMAGE}
                    aria-hidden="true"
                    onClick={onImageClick}
                    isSnaccStorefrontV2={isSnaccStorefrontV2}
                >
                    <Image
                        cloudinaryId={imageId}
                        errorFallbackSrc={CROSS_SELL_M2_ITEM_PLACEHOLDER}
                        fit="cover"
                        width="44px"
                        cloudinaryHeight={isSnaccStorefrontV2 ? 133 * 3 : 72 * 2}
                    />
                </ImageContainer>

                {/* Conditionally render lock or plus icon on top-left */}
                {isLocked ? (
                    <LockIconContainer onClick={handleLockIconClick}>
                        <LockFilledSmoothIcon fill={theme.colors.light.text_Low_Emphasis} />
                    </LockIconContainer>
                ) : (
                    <AddButtonContainer
                        onClick={onAddButtonClick}
                        aria-label={accessibilityTextAddButton}
                        data-testid={CROSS_SELL_TEST_IDS.ADD_BUTTON}
                        role="button"
                    >
                        <CrossSellM2AddItem />
                    </AddButtonContainer>
                )}

                <CrossSellM2ItemDetailsBSContainer index={index} />

                {showBottomSheet && (
                    <HalfCard
                        isOpen={showBottomSheet}
                        onClose={handleClose}
                        hasCustomPadding
                        root={`#${containerIdForCrossSellM2ItemDetailsBS}_${index}`}
                        variant={isPopCart && isSnaccStorefrontV2 ? "v3" : "default"}
                    >
                        <CrossSellM2ItemDetails
                            name={name}
                            finalPrice={finalPrice}
                            basePrice={basePrice}
                            imageId={imageId}
                            vegType={vegType}
                            description={description}
                            isSnaccStorefrontV2={isSnaccStorefrontV2}
                        />
                    </HalfCard>
                )}

                {/* Add the unlock popup */}
                {showUnlockPopup && (
                    <UnlockPopup
                        isOpen={showUnlockPopup}
                        onClose={handleCloseUnlockPopup}
                        itemName={name}
                        unlockAmount={unlockAmount}
                        onExploreClick={handleExploreMenu}
                        imageId={imageId}
                        vegType={vegType}
                        basePrice={basePrice}
                        finalPrice={finalPrice}
                        showPriceDrop={basePrice !== finalPrice}
                        onImpressionMagicItemsUnlockPopup={onImpressionMagicItemsUnlockPopup}
                        onClickDismissMagicItemsUnlockPopup={onClickDismissMagicItemsUnlockPopup}
                        onClickExploreMenuMagicItems={onClickExploreMenuMagicItems}
                        itemPresentationAttributes={itemPresentationAttributes}
                        itemDiscountPercentage={itemDiscountPercentage}
                    />
                )}
            </Container>
        </ElementVisibilitySensor>
    );
};

export default memo(CrossSellM2Item);
